import { useEffect, useState } from "react";
import { Download, Pencil, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SubmissionAttachment } from "@/components/SubmissionAttachment";
import { SubmissionStatusBadge } from "@/components/SubmissionStatusBadge";

const dateTime = (value) => value ? new Date(value).toLocaleString() : "—";

export const ParentSubmissionReview = ({ assignment, submission, onChanged, startEditingGrade = false }) => {
  const [grade, setGrade] = useState("");
  const [feedback, setFeedback] = useState("");
  const [parentNotes, setParentNotes] = useState("");
  const [returnNote, setReturnNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editingGrade, setEditingGrade] = useState(startEditingGrade);

  const resetFields = () => {
    setGrade(submission?.grade != null ? String(submission.grade) : "");
    setFeedback(submission?.feedback || "");
    setParentNotes(submission?.parent_notes || "");
    setReturnNote("");
  };
  useEffect(() => { resetFields(); setEditingGrade(startEditingGrade); }, [submission, startEditingGrade]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!submission) return <div className="text-sm text-slate-500">Submission details unavailable.</div>;
  const attachments = submission.attachments || [];
  const hasWork = Boolean(submission.work_text?.trim() || submission.work_link?.trim() || attachments.length);
  const isGraded = submission.status === "graded";

  const saveGrade = async () => {
    if (!grade.trim()) return toast.error("Enter a grade or score");
    setSaving(true);
    try {
      await api.post(`/assignments/${assignment.id}/submission/grade`, { grade, feedback, parent_notes: parentNotes });
      toast.success(isGraded ? "Grade updated" : "Grade saved"); setEditingGrade(false); await onChanged();
    } catch (error) { toast.error(error?.response?.data?.detail || "Could not save grade"); }
    finally { setSaving(false); }
  };

  const returnChanges = async () => {
    if (!returnNote.trim()) return toast.error("Add a short return note first");
    setSaving(true);
    try {
      await api.post(`/assignments/${assignment.id}/submission/return`, { note: returnNote });
      toast.success("Returned for changes"); await onChanged();
    } catch (error) { toast.error(error?.response?.data?.detail || "Could not return submission"); }
    finally { setSaving(false); }
  };

  const downloadPacket = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/assignments/${assignment.id}/submission/packet`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = `${assignment.title}-submission.pdf`; anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(error?.response?.data?.detail || "Could not create submission packet"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 space-y-4" data-testid={`parent-submission-${assignment.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Student submission</div>
          <div className="text-xs text-slate-500 mt-1">Submitted {dateTime(submission.submitted_at)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubmissionStatusBadge status={submission.status} testId={`parent-submission-status-${assignment.id}`} />
          {submission.status !== "not_started" && (
            <button type="button" data-testid={`submission-packet-${assignment.id}`} onClick={downloadPacket} disabled={downloading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-slate-200 text-xs font-semibold text-slate-700 disabled:opacity-50">
              <Download size={13} /> {downloading ? "Building PDF…" : "Download Submission Packet"}
            </button>
          )}
        </div>
      </div>

      {!hasWork && ["submitted", "returned", "graded"].includes(submission.status) && (
        <div data-testid={`submission-no-work-${assignment.id}`} className="p-4 rounded-xl bg-amber-50 border border-amber-200 font-semibold text-amber-900">No work file, link, or typed response was attached.</div>
      )}
      {submission.work_text && (
        <div data-testid={`submission-work-text-${assignment.id}`} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Typed work</div>
          <div className="mt-2 whitespace-pre-wrap text-slate-800">{submission.work_text}</div>
        </div>
      )}
      {submission.work_link && (
        <div><div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Work link</div>
          <a data-testid={`submission-work-link-${assignment.id}`} href={submission.work_link} target="_blank" rel="noreferrer" className="text-teal-700 font-semibold break-all hover:underline">{submission.work_link} ↗</a>
        </div>
      )}
      {attachments.length > 0 && (
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Submitted files</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`submission-files-${assignment.id}`}>
            {attachments.map((file, index) => <SubmissionAttachment key={file.file_id || index} file={file} index={index} prefix={`parent-submission-${assignment.id}`} />)}
          </div>
        </div>
      )}
      {submission.reflection && (
        <div data-testid={`submission-reflection-${assignment.id}`} className="p-4 rounded-xl bg-sky-50 border border-sky-100">
          <div className="text-xs text-sky-700 uppercase tracking-widest">Reflection</div>
          <div className="mt-2 whitespace-pre-wrap text-slate-800">{submission.reflection}</div>
        </div>
      )}
      {submission.return_note && <div className="p-3 rounded-xl bg-amber-50 border border-amber-200"><b>Return note:</b> {submission.return_note}</div>}
      {submission.grade != null && String(submission.grade) !== "" && (
        <div className="flex flex-wrap items-center gap-3" data-testid={`submission-grade-${assignment.id}`}>
          <span><b>Grade / score:</b> {String(submission.grade)}</span>
          {isGraded && !editingGrade && (
            <button type="button" data-testid={`edit-grade-${assignment.id}`} onClick={() => setEditingGrade(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-teal-200 text-xs font-semibold text-teal-800 hover:bg-teal-50"><Pencil size={12} /> Edit Grade</button>
          )}
        </div>
      )}
      {submission.feedback && <div data-testid={`submission-feedback-${assignment.id}`}><b>Student-visible feedback:</b> {submission.feedback}</div>}
      {submission.parent_notes && <div data-testid={`submission-parent-notes-${assignment.id}`} className="p-3 rounded-xl bg-slate-50 border border-slate-200"><b>Private parent notes:</b> {submission.parent_notes}</div>}

      {(submission.history || []).length > 0 && (
        <div data-testid={`submission-history-${assignment.id}`}>
          <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Revision & submission history</div>
          <div className="space-y-2">
            {submission.history.map((event, index) => (
              <details key={event.id || index} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <summary data-testid={`submission-history-event-${assignment.id}-${index}`} className="cursor-pointer font-semibold text-slate-700 capitalize">
                  {event.action?.replace("_", " ")} · {dateTime(event.timestamp)}
                </summary>
                <div className="mt-2 text-xs text-slate-600 space-y-1">
                  {event.note && <div>Note: {event.note}</div>}
                  {event.grade && <div>Grade: {event.previous_grade != null ? `${event.previous_grade} → ` : ""}{event.grade}</div>}
                  {event.snapshot?.work_text && <div className="whitespace-pre-wrap">Typed work: {event.snapshot.work_text}</div>}
                  {event.snapshot?.work_link && <div>Link: {event.snapshot.work_link}</div>}
                  {event.snapshot?.reflection && <div className="whitespace-pre-wrap">Reflection: {event.snapshot.reflection}</div>}
                  {event.snapshot?.attachments?.length > 0 && <div>Files: {event.snapshot.attachments.map((file)=>file.file_name).join(", ")}</div>}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {(submission.status === "submitted" || (isGraded && editingGrade)) && (
        <div className="p-4 rounded-xl border border-teal-200 bg-teal-50/50 space-y-3" data-testid={`grading-controls-${assignment.id}`}>
          <div className="font-display font-semibold text-lg text-slate-900">{isGraded ? "Edit saved grade" : "Grade this submission"}</div>
          <label className="block text-sm font-semibold">Grade / score
            <input data-testid={`grading-grade-${assignment.id}`} value={grade} onChange={(event)=>setGrade(event.target.value)} placeholder="A, 92/100, Pass…"
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
          </label>
          <label className="block text-sm font-semibold">Feedback visible to student
            <textarea data-testid={`grading-feedback-${assignment.id}`} rows={3} value={feedback} onChange={(event)=>setFeedback(event.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
          </label>
          <label className="block text-sm font-semibold">Private parent notes
            <textarea data-testid={`grading-parent-notes-${assignment.id}`} rows={3} value={parentNotes} onChange={(event)=>setParentNotes(event.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
          </label>
          {isGraded ? (
            <div className="flex flex-wrap gap-3 justify-end">
              <button type="button" data-testid={`cancel-edit-grade-${assignment.id}`} onClick={() => { resetFields(); setEditingGrade(false); }} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold">Cancel</button>
              <button type="button" data-testid={`save-grade-${assignment.id}`} onClick={saveGrade} disabled={saving || !grade.trim()}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-40"><Save size={15} /> Save Changes</button>
            </div>
          ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold">Return note
                <textarea data-testid={`return-note-${assignment.id}`} rows={3} value={returnNote} onChange={(event)=>setReturnNote(event.target.value)} placeholder="What should the student change?"
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-amber-200 bg-white" />
              </label>
              <button type="button" data-testid={`return-submission-${assignment.id}`} onClick={returnChanges} disabled={saving || !returnNote.trim()}
                className="mt-2 w-full inline-flex justify-center items-center gap-2 px-4 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-40"><RotateCcw size={15} /> Return for Changes</button>
            </div>
            <button type="button" data-testid={`save-grade-${assignment.id}`} onClick={saveGrade} disabled={saving || !grade.trim()}
              className="self-end inline-flex justify-center items-center gap-2 px-4 py-3 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-40"><Save size={15} /> Save Grade</button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};